import React from 'react';
import { MessageCircle, Upload, Plus, RefreshCw, Trash2, Settings } from 'lucide-react';
import { formatRelativeTime } from '../../../lib/utils';

interface Activity {
  id: string;
  type: 'chat' | 'upload' | 'domain_create' | 'sync' | 'delete' | 'settings';
  description: string;
  timestamp: string;
}

interface RecentActivityProps {
  activities?: Activity[];
  loading?: boolean;
}

const getActivityIcon = (type: Activity['type']) => {
  switch (type) {
    case 'chat':
      return <MessageCircle size={18} />;
    case 'upload':
      return <Upload size={18} />;
    case 'domain_create':
      return <Plus size={18} />;
    case 'sync':
      return <RefreshCw size={18} />;
    case 'delete':
      return <Trash2 size={18} />;
    case 'settings':
      return <Settings size={18} />;
    default:
      return <Settings size={18} />;
  }
};

const getActivityIconClasses = (type: Activity['type']): string => {
  switch (type) {
    case 'chat':
      return 'bg-blue-50 text-blue-600';
    case 'upload':
      return 'bg-green-50 text-green-600';
    case 'domain_create':
      return 'bg-cyan-50 text-cyan-600';
    case 'sync':
      return 'bg-amber-50 text-amber-600';
    case 'delete':
      return 'bg-red-50 text-red-600';
    default:
      return 'bg-gray-100 text-gray-500';
  }
};

// デモ用のダミーデータ（実際のAPIが実装されたら置き換え）
const dummyActivities: Activity[] = [
  { id: '1', type: 'chat', description: 'example.com でチャットセッションが開始されました', timestamp: new Date(Date.now() - 1000 * 60 * 5).toISOString() },
  { id: '2', type: 'upload', description: 'manual.pdf をアップロードしました', timestamp: new Date(Date.now() - 1000 * 60 * 30).toISOString() },
  { id: '3', type: 'sync', description: 'example.com の学習データ同期が完了しました', timestamp: new Date(Date.now() - 1000 * 60 * 60).toISOString() },
  { id: '4', type: 'domain_create', description: 'test.example.com を登録しました', timestamp: new Date(Date.now() - 1000 * 60 * 60 * 3).toISOString() },
  { id: '5', type: 'settings', description: 'BOT設定を更新しました', timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString() },
];

export const RecentActivity: React.FC<RecentActivityProps> = ({
  activities = dummyActivities,
  loading = false,
}) => {
  if (loading) {
    return (
      <div className="rounded-lg border border-border bg-card">
        <div className="px-6 py-4 border-b border-border">
          <div className="animate-pulse bg-gray-200 rounded h-5 w-32" />
        </div>
        <div className="px-6 py-4">
          <ul className="divide-y divide-border">
            {[1, 2, 3, 4, 5].map((i) => (
              <li key={i} className="flex items-center gap-3 py-2">
                <div className="flex-shrink-0">
                  <div className="animate-pulse bg-gray-200 rounded-full w-6 h-6" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="animate-pulse bg-gray-200 rounded h-4 w-4/5 mb-1" />
                  <div className="animate-pulse bg-gray-200 rounded h-3 w-2/5" />
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border bg-card">
      <div className="px-6 py-4 border-b border-border">
        <p className="text-sm font-semibold text-text-primary">最近のアクティビティ</p>
      </div>
      <div className="px-6 py-4">
        {activities.length === 0 ? (
          <div className="py-8 text-center">
            <p className="text-sm text-text-secondary">アクティビティはありません</p>
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {activities.slice(0, 5).map((activity) => (
              <li key={activity.id} className="flex items-center gap-3 py-2">
                <div className={`flex-shrink-0 p-1 rounded flex items-center justify-center ${getActivityIconClasses(activity.type)}`}>
                  {getActivityIcon(activity.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-text-primary truncate">{activity.description}</p>
                  <span className="text-xs text-text-secondary">{formatRelativeTime(activity.timestamp)}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
